import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import { buildAzureAuth, resolveAzureAccountMail } from './loginIdentify'

// The one way an Entra token becomes a platform session, whatever produced the token (the silent
// request after a popup/redirect sign-in, or the Azure button's token popup): exchange it for the
// hemav api_key, store the Entra identity with the session, patch the Graph mailbox address in and
// load the profile. Resolves when the profile fetch started; rejects when the hemav login failed.
export const completeAzureLogin = ({ response, account, fetchApiKeyWithOauthToken, fetchProfileAndRedirect, session }) => {
  const accessToken = get(response, 'accessToken')
  const signedIn = get(response, 'account') || account
  // the session cookie records the Entra identity with the api_key (receiveLogin writes it before the
  // profile fetch, so identify always sees the azure session)
  const loginDone = fetchApiKeyWithOauthToken({ oauthData: { accessToken }, azure: buildAzureAuth(signedIn) })
  resolveAzureAccountMail({ accessToken, session, loginDone })
  return loginDone.then(() => {
    fetchProfileAndRedirect()
  })
}

// At most one Azure token-to-session attempt at a time on a Login page. The guard is held until the
// hemav login settles, not just until the token arrives: while get_azure_api_key runs the form and the
// Azure button stay on screen, and a second attempt would run a second exchange, a second setToken and
// redirect, and patch its Graph mail into the other login. (Rendering the loading state instead would
// unmount the button and, on a failed exchange, remount it into another silent attempt — a loop.)
// `run` returns null when an attempt is already in flight.
export const createAzureLoginFlight = () => {
  let inFlight = false
  return {
    isInFlight: () => inFlight,
    run: task => {
      if (inFlight) return null
      inFlight = true
      let attempt
      try {
        attempt = Promise.resolve(task())
      } catch (error) {
        attempt = Promise.reject(error)
      }
      return attempt.finally(() => {
        inFlight = false
      })
    }
  }
}

// After a sign-in (isAuthenticated): ask for the token silently and finish the login. A silent failure
// only logs — no popup from here, without a user gesture the browser blocks it; the cached account
// stays, so one click on the Azure button asks for the token interactively. Resolves once settled.
export const startSilentAzureLogin = ({ instance, account, request, complete, flight }) =>
  flight.run(() =>
    instance.acquireTokenSilent({ ...request, account }).then(
      response => complete({ response, account }),
      error => {
        console.warn('Azure silent token request failed — waiting for the Azure button', error)
      }
    )
  ) || Promise.resolve()

// The Azure button. With no cached MSAL account it opens the sign-in popup; the silent request then
// finishes the login (the popup is outside the flight, so that silent attempt is never shadowed by it).
// With a cached account the silent request has already been tried and failed (typically the daily
// re-login: the SPA refresh token is gone and the hidden Entra iframe is blocked), so the click — a user
// gesture, the only thing a popup is allowed from — asks for the token interactively and finishes the
// login itself. Does nothing while a platform session exists or another attempt is in flight.
export const startAzureButtonLogin = ({ instance, accounts, hasSession, request, complete, flight }) => {
  if (hasSession) return Promise.resolve()
  if (isEmpty(accounts)) return instance.loginPopup(request)
  const account = accounts[0]
  return flight.run(() => instance.acquireTokenPopup({ ...request, account }).then(response => complete({ response, account }))) || Promise.resolve()
}
